import React, { useState } from 'react'
import { Database, ChevronDown, Info } from 'lucide-react'
import { DaxQuery } from '../../api/chatService'

interface DaxQueriesBoxProps {
  daxQueries: DaxQuery[]
}

// "DimLocation[LocationName]" -> "LocationName"; "[Revenue]" -> "Revenue"
const cleanHeader = (key: string): string =>
  key.replace(/^.*\[/, '').replace(/\]$/, '')

const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number')
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 })
  return String(value)
}

/** Results table (or error / empty note) shown under a query's DAX. */
const DaxResults: React.FC<{ query: DaxQuery }> = ({ query }) => {
  if (query.error) {
    return (
      <div className="dax-item__note dax-item__note--error">
        <Info size={13} />
        {query.error}
      </div>
    )
  }

  const rows = query.rows || []
  if (rows.length === 0) {
    return (
      <div className="dax-item__note">
        <Info size={13} />
        Query returned no rows.
      </div>
    )
  }

  const columns = Object.keys(rows[0])
  const total = query.row_count ?? rows.length
  const capped = total > rows.length

  return (
    <div className="dax-results">
      <div className="dax-results__caption">
        Results ({total.toLocaleString()} {total === 1 ? 'row' : 'rows'})
      </div>
      <div className="dax-results__scroll">
        <table className="dax-results__table">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col}>{cleanHeader(col)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map(col => (
                  <td key={col}>{formatCell(row[col])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {capped && (
        <div className="dax-results__note">
          Showing first {rows.length.toLocaleString()} of{' '}
          {total.toLocaleString()} rows.
        </div>
      )}
    </div>
  )
}

/**
 * Collapsible box listing the DAX queries the analytics agent ran, with a short
 * title of what each did. The query results are intentionally not shown.
 */
const DaxQueriesBox: React.FC<DaxQueriesBoxProps> = ({ daxQueries }) => {
  const [open, setOpen] = useState(false)
  const [openItems, setOpenItems] = useState<Record<number, boolean>>({})

  if (!daxQueries || daxQueries.length === 0) return null

  const toggleItem = (index: number) =>
    setOpenItems(prev => ({ ...prev, [index]: !prev[index] }))

  return (
    <div className={`dax-box ${open ? 'dax-box--open' : ''}`}>
      <button
        type="button"
        className="dax-box__header"
        onClick={() => setOpen(prev => !prev)}
        aria-expanded={open}
      >
        <Database size={16} className="dax-box__icon" />
        <span className="dax-box__title">DAX queries run</span>
        <span className="dax-box__count">{daxQueries.length}</span>
        <ChevronDown size={16} className="dax-box__chevron" />
      </button>

      {open && (
        <div className="dax-box__list">
          {daxQueries.map((query, index) => {
            const isItemOpen = !!openItems[index]
            return (
              <div
                key={index}
                className={`dax-item ${isItemOpen ? 'dax-item--open' : ''}`}
              >
                <button
                  type="button"
                  className="dax-item__head"
                  onClick={() => toggleItem(index)}
                  aria-expanded={isItemOpen}
                >
                  <span className="dax-item__num">{index + 1}</span>
                  <span className="dax-item__title">
                    {query.model_name && (
                      <span className="dax-item__model">
                        {query.model_name}:{' '}
                      </span>
                    )}
                    {query.title}
                  </span>
                  <ChevronDown size={15} className="dax-item__chevron" />
                </button>

                {isItemOpen && (
                  <div className="dax-item__body">
                    <pre className="dax-item__code">{query.dax}</pre>
                    <DaxResults query={query} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default DaxQueriesBox
